import { createSelector } from 'reselect';
import type { State } from '~/store';
import { selectWorkflowDataSnapshotById } from './history';
import {
  selectActionByPublicId,
  selectActions,
  selectDataRoutes,
  selectFocusedAction,
  selectPublicVersion,
  selectThresholds,
  selectWorkflowData,
  selectWorkflowProps,
} from './general';
import type { SnippetVersion } from 'flow/service/snippets';
import {
  getIsInSnippetPastePendingMode,
  getIsInSnippetPreviewMode,
  getIsInSnippetReviewMode,
  getIsLinkedSnippetUpgradeOp,
} from 'flow/service/snippets';
import generateMergeDiff from 'flow/service/changelog/formatters/mergeDropdownFormatter/mergeDiff';
import type { RuleBuilderRule } from 'flow/service/rules';
import type {
  Action, DataRoute, PublicId, Threshold,
} from 'flow/store/engine/types';
import { snippetInsertionErrorType, snippetOperation } from 'flow/store/redux/types';
import type { SnippetInsertionError } from 'flow/store/redux/types';
import {
  getSnippetVersionFromWorkflowData,
  isChildOfSnippetAction,
  isExternalActionType,
  isSnippetChildAction,
} from 'flow/service/actions/snippets';
import { getActionName, indexActionsByPublicId, isActionOfType } from 'flow/service/actions';
import { getThresholdsForActions, indexThresholdsByPublicId } from 'flow/service/thresholds';
import { getDataRoutesForActions, indexDataRoutesByPublicId } from 'flow/service/dataRoutes';
import { ContainerType } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import { TAG_NAME_MAX_LENGTH } from 'flow/service/actions/tags/constants';


export const selectSnippetMode = createSelector(
  (state: State) => state.workflowEditor.ui.snippet.mode,
  mode => mode,
);

export function selectSnippetActiveOperation(state: State) {
  return state.workflowEditor.ui.snippet.activeOperation;
}

export function selectIsPreviewSnippetMode(state: State) {
  const snippetMode = selectSnippetMode(state);
  return snippetMode ? getIsInSnippetPreviewMode(snippetMode) : false;
}

export function selectIsReviewSnippetMode(state: State) {
  const snippetMode = selectSnippetMode(state);
  return snippetMode ? getIsInSnippetReviewMode(snippetMode) : false;
}

export function selectIsPastePendingSnippetMode(state: State) {
  const snippetMode = selectSnippetMode(state);
  return snippetMode ? getIsInSnippetPastePendingMode(snippetMode) : false;
}

export function selectSnippetIncompatibleActions(state: State) {
  return state.workflowEditor.ui.snippet.incompatibleActions;
}

export const selectPreSnippetWorkflowDataSnapshotId = createSelector(
  (state: State) => state.workflowEditor.ui.snippet.preSnippetWorkflowDataSnapshotId,
  preInsertionSnapshotId => preInsertionSnapshotId,
);

export function selectPreSnippetWorkflowDataSnapshot(state: State) {
  const snapshotId = selectPreSnippetWorkflowDataSnapshotId(state);
  if (!snapshotId) {
    return null;
  }
  return selectWorkflowDataSnapshotById(snapshotId)(state);
}

export function selectPreSnippetWorkflowPropsSnapshot(state: State) {
  return state.workflowEditor.ui.snippet.preSnippetWorkflowPropsSnapshot;
}

export const selectSnippetMetadata = createSelector(
  (state: State) => state.workflowEditor.snippet,
  snippet => snippet.metadata,
);

export const selectIsSnippetVersionPublished = createSelector(
  [selectPublicVersion, selectSnippetMetadata],
  (publicVersion, metadata) => {
    if (!metadata) {
      return false;
    }

    if (publicVersion === null) {
      return false;
    }

    return metadata.published_version === publicVersion;
  },
);

export const selectMergeDiff = createSelector(
  [
    selectPreSnippetWorkflowDataSnapshot,
    selectWorkflowData,
    selectWorkflowProps,
    selectSnippetActiveOperation,
  ],
  (preInsertSnapshot, workflowData, workflowProps, { operation: snippetOp }) => {
    if (!preInsertSnapshot) return null;

    const isLinkedSnippetUpgrade = getIsLinkedSnippetUpgradeOp(snippetOp);
    return generateMergeDiff({
      A: {
        props: workflowProps,
        version: preInsertSnapshot.data,
      },
      B: {
        props: workflowProps,
        version: workflowData,
      },
      isLinkedSnippetUpgrade,
    });
  },
);

export const selectSnippetChangelogVersions = createSelector(
  [
    selectSnippetActiveOperation,
    selectPreSnippetWorkflowDataSnapshot,
    selectWorkflowData,
  ],
  (snippetActiveOperation, preInsertSnapshot, workflowData) => {
    const { operation, snippetActionPublicId } = snippetActiveOperation;

    if (operation !== snippetOperation.upgradeLinkedSnippet) {
      return { currentSnippetVersion: null, updatedSnippetVersion: null };
    }

    return {
      currentSnippetVersion: getSnippetVersionFromWorkflowData({
        actions: preInsertSnapshot?.data?.actions,
        snippetActionPublicId,
      }),
      updatedSnippetVersion: getSnippetVersionFromWorkflowData({
        actions: workflowData.actions,
        snippetActionPublicId,
      }),
    };
  },
);

export const selectSnippetsWithUpdates = createSelector(
  [
    selectWorkflowData,
    selectWorkflowProps,
  ],
  (workflowData, workflowProps) => {
    if (!workflowProps?.snippets) return [];

    const snippetActions = Object.values(workflowData?.actions ?? {})
      .filter(action => isActionOfType(action, ActionType.SNIPPET));

    return snippetActions.filter(snippetAction => {
      const {
        token: snippetToken,
        publicVersion: currSnippetVersion,
      } = snippetAction.snippet;

      const publishedSnippetVersion = workflowProps.snippets[snippetToken]?.published_version;
      return publishedSnippetVersion != null && currSnippetVersion !== publishedSnippetVersion;
    });
  },
);

/**
 * Select actions that were removed by the snippet insertion, resolved from
 * the pre-insert snapshot since they no longer exist in the current workflow.
 */
export const selectMergeDiffRemovedActions = createSelector(
  [selectMergeDiff, selectPreSnippetWorkflowDataSnapshot],
  (mergeDiff, preSnapshot): Action[] => {
    if (!mergeDiff || !preSnapshot?.data?.actions) return [];
    const { actions } = preSnapshot.data;
    return Array.from(mergeDiff.removed)
      .map(publicId => actions[publicId])
      .filter(action => action !== undefined);
  },
);

export function selectIsMergeModified(
  publicId: PublicId,
) {
  return function selector(state: State) {
    const diff = selectMergeDiff(state);

    if (!diff) return false;

    return diff.modified.has(publicId);
  };
}

export function selectIsMergeAdded(
  publicId: PublicId,
) {
  return function selector(state: State) {
    const diff = selectMergeDiff(state);

    if (!diff) return false;

    return diff.added.has(publicId);
  };
}

export function selectIsMergeRemoved(
  publicId: PublicId,
) {
  return function selector(state: State) {
    const diff = selectMergeDiff(state);

    if (!diff) return false;

    return diff.removed.has(publicId);
  };
}

export function selectIsRuleDiffed(
  ruleId: RuleBuilderRule['id'],
  sourceActionId: Action['publicId'],
) {
  return function selector(state: State) {
    return selectIsMergeModified(sourceActionId)(state)
      && (selectIsMergeAdded(ruleId)(state) || selectIsMergeModified(ruleId)(state));
  };
}


/**
 * Selects all actions that have an external type, regardless of their existing association with a snippet
 */
export function selectExternalWorkflowActions(state: State) {
  const actions = selectActions(state) ?? {};
  return indexActionsByPublicId(
    Object.values(actions).filter(action => isExternalActionType(action.type)),
  );
}

function selectSnippetChildActions(snippetParentActionPublicId: PublicId) {
  return function selector(state: State): Action[] {
    const actions = selectActions(state) ?? {};
    const snippetChildActions = Object.values(actions)
      .filter(action => isChildOfSnippetAction({ action, snippetParentActionPublicId }));

    return snippetChildActions;
  };
}

function selectSnippetThresholds(snippetParentActionPublicId: PublicId) {
  return function selector(state: State): Threshold[] {
    const thresholds = selectThresholds(state) ?? {};

    const snippetActions = selectSnippetChildActions(snippetParentActionPublicId)(state);
    const snippetThresholds = snippetActions.flatMap(snippetAction => getThresholdsForActions({
      action: snippetAction,
      thresholds,
    }));


    return snippetThresholds;
  };
}

function selectSnippetDataRoutes(snippetParentActionPublicId: PublicId) {
  return function selector(state: State): DataRoute[] {
    const dataRoutes = selectDataRoutes(state) ?? {};

    const snippetActions = selectSnippetChildActions(snippetParentActionPublicId)(state);
    const snippetDataRoutes = snippetActions.flatMap(snippetAction => getDataRoutesForActions({
      action: snippetAction,
      dataRoutes,
    }));


    return snippetDataRoutes;
  };
}

export function selectLocalSnippetData(args: { snippetActionPublicId: PublicId }) {
  return function selector(state: State): SnippetVersion['data'] {
    const { snippetActionPublicId } = args;

    const snippetParentAction = selectActionByPublicId(snippetActionPublicId)(state);
    if (!snippetParentAction) {
      throw new Error(`Parent snippet action with id ${snippetActionPublicId} not found`);
    }

    const snippetActions = selectSnippetChildActions(snippetActionPublicId)(state);
    const snippetThresholds = selectSnippetThresholds(snippetActionPublicId)(state);
    const snippetDataRoutes = selectSnippetDataRoutes(snippetActionPublicId)(state);

    return {
      actions: indexActionsByPublicId([...snippetActions, snippetParentAction]),
      dataRoutes: indexDataRoutesByPublicId(snippetDataRoutes),
      thresholds: indexThresholdsByPublicId(snippetThresholds),
    };
  };
}

/**
 * Container type of the focused action:
 * As of right now, the only container type is SNIPPET
 */
export function selectFocusedActionContainerType(
  state: State,
): typeof ContainerType[keyof typeof ContainerType] | null {
  const focusedAction = selectFocusedAction(state);
  if (!focusedAction) return null;

  const isPartOfSnippet = isActionOfType(focusedAction, ActionType.SNIPPET)
    || isSnippetChildAction(focusedAction);
  if (isPartOfSnippet) return ContainerType.SNIPPET;

  return null;
}

/**
 * Selects insertion errors introduced by a snippet insertion
 * Designed to be extensible to additional error types beyond tag name length.
 */
export const selectSnippetInsertionErrors = createSelector(
  [selectMergeDiff, selectActions],
  (mergeDiff, actions): SnippetInsertionError[] => {
    if (!mergeDiff || !actions) return [];

    const errors: SnippetInsertionError[] = [];
    const impactedIds = new Set([...mergeDiff.added, ...mergeDiff.modified]);

    for (const publicId of impactedIds) {
      const action = actions[publicId];
      if (action && isActionOfType(action, ActionType.TAG)) {
        const name = getActionName(action);
        if (name.length > TAG_NAME_MAX_LENGTH) {
          errors.push({
            type: snippetInsertionErrorType.tagNameTooLong,
            actionPublicId: publicId,
          });
        }
      }
    }

    return errors;
  },
);

export const selectFocusedActionHasInsertionError = createSelector(
  [selectFocusedAction, selectSnippetInsertionErrors],
  (focusedAction, errors): boolean => {
    if (!focusedAction || errors.length === 0) return false;
    return errors.some(e => e.actionPublicId === focusedAction.publicId);
  },
);
